import { useState, useRef } from 'react';
import { Category } from '@/hooks/useCategories';
import { useCategories } from '@/hooks/useCategories';

const ICON_OPTIONS = [
  'ri-heart-pulse-line', 'ri-chat-smile-2-line', 'ri-restaurant-2-line',
  'ri-trophy-line', 'ri-lightbulb-line', 'ri-home-heart-line',
  'ri-heart-line', 'ri-leaf-line', 'ri-shield-check-line',
  'ri-star-line', 'ri-book-open-line', 'ri-user-heart-line',
  'ri-community-line', 'ri-seedling-line', 'ri-award-line',
  'ri-camera-line', 'ri-palette-line', 'ri-music-line',
];

interface CategoryFormState {
  label: string;
  icon: string;
  image: string;
  description: string;
  active: boolean;
}

const emptyForm = (): CategoryFormState => ({
  label: '', icon: 'ri-heart-pulse-line', image: '', description: '', active: true,
});

export default function CategoryEditor() {
  const {
    categories, addCategory, updateCategory, deleteCategory, toggleActive,
  } = useCategories();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState<CategoryFormState>(emptyForm());
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const openEdit = (cat: Category) => {
    setEditingId(cat.id);
    setIsCreating(false);
    setForm({ label: cat.label, icon: cat.icon, image: cat.image, description: cat.description, active: cat.active });
    setSaved(false);
  };

  const openCreate = () => {
    setEditingId(null);
    setIsCreating(true);
    setForm(emptyForm());
    setSaved(false);
  };

  const cancelForm = () => {
    setEditingId(null);
    setIsCreating(false);
    setForm(emptyForm());
  };

  const handleSave = () => {
    if (!form.label.trim()) return;
    if (isCreating) {
      addCategory({ ...form, label: form.label.trim() });
    } else if (editingId) {
      updateCategory(editingId, { ...form, label: form.label.trim() });
    }
    setSaved(true);
    setTimeout(() => { setSaved(false); cancelForm(); }, 1200);
  };

  const handleImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setForm(p => ({ ...p, image: ev.target?.result as string }));
    reader.readAsDataURL(file);
  };

  const activeCount = categories.filter(c => c.active).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">Categorias</h2>
          <p className="text-foreground-500 text-sm mt-1">
            {activeCount} ativa{activeCount !== 1 ? 's' : ''} de {categories.length} categorias
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-bold px-4 py-2 rounded-md transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i> Nova Categoria
        </button>
      </div>

      {/* Inline form */}
      {(isCreating || editingId) && (
        <div className="bg-background-50 border-2 border-primary-200 rounded-xl p-6 space-y-4">
          <h3 className="font-bold text-foreground-950 text-base flex items-center gap-2">
            <i className={`${isCreating ? 'ri-add-circle-line' : 'ri-edit-line'} text-primary-500`}></i>
            {isCreating ? 'Nova Categoria' : 'Editar Categoria'}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Label */}
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Nome *</label>
              <input
                type="text"
                value={form.label}
                onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
                placeholder="Ex: Saúde"
                className="w-full border border-background-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary-400 bg-white"
              />
            </div>

            {/* Icon */}
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Ícone</label>
              <div className="flex items-center gap-2">
                <select
                  value={form.icon}
                  onChange={e => setForm(p => ({ ...p, icon: e.target.value }))}
                  className="flex-1 border border-background-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:border-primary-400 bg-white cursor-pointer"
                >
                  {ICON_OPTIONS.map(ic => (
                    <option key={ic} value={ic}>{ic.replace('ri-', '').replace(/-line|-fill/, '')}</option>
                  ))}
                </select>
                <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className={`${form.icon} text-primary-500 text-xl`}></i>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Descrição</label>
              <input
                type="text"
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Breve descrição da categoria..."
                maxLength={120}
                className="w-full border border-background-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary-400 bg-white"
              />
            </div>
          </div>

          {/* Image */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide">Imagem da Categoria</label>
              <input
                type="text"
                value={form.image}
                onChange={e => setForm(p => ({ ...p, image: e.target.value }))}
                placeholder="URL da imagem..."
                className="w-full border border-background-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:border-primary-400 bg-white"
              />
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-background-300 rounded-lg py-3 text-xs text-foreground-400 hover:border-primary-300 hover:text-primary-500 transition-colors cursor-pointer flex items-center justify-center gap-1"
              >
                <i className="ri-upload-cloud-2-line"></i> Upload
              </button>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleImageFile} className="hidden" />
            </div>
            {form.image && (
              <div className="w-full h-28 rounded-lg overflow-hidden border border-background-200">
                <img src={form.image} alt="preview" className="w-full h-full object-cover object-top" />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button onClick={cancelForm} className="px-4 py-2 text-sm border border-background-300 rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer whitespace-nowrap transition-colors">
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={!form.label.trim()}
              className={`px-5 py-2 text-sm font-bold rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 disabled:opacity-50 ${
                saved ? 'bg-green-500 text-white' : 'bg-primary-500 hover:bg-primary-600 text-white'
              }`}
            >
              {saved ? <><i className="ri-checkbox-circle-line"></i> Salvo!</> : <><i className="ri-save-line"></i> Salvar</>}
            </button>
          </div>
        </div>
      )}

      {/* Category Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map(cat => (
          <div
            key={cat.id}
            className={`border rounded-xl overflow-hidden transition-all ${
              cat.active ? 'border-background-200 bg-background-50' : 'border-background-200 bg-background-100 opacity-60'
            }`}
          >
            {/* Image preview or icon banner */}
            <div className="relative h-24 overflow-hidden bg-background-200">
              {cat.image ? (
                <img src={cat.image} alt={cat.label} className="w-full h-full object-cover object-top" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-primary-100">
                  <i className={`${cat.icon} text-4xl text-primary-300`}></i>
                </div>
              )}
              {/* Active badge */}
              <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                cat.active ? 'bg-green-100 text-green-700' : 'bg-background-200 text-foreground-500'
              }`}>
                {cat.active ? 'Ativa' : 'Inativa'}
              </div>
            </div>

            <div className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                  <i className={`${cat.icon} text-primary-500 text-sm`}></i>
                </div>
                <span className="font-bold text-foreground-950 text-sm">{cat.label}</span>
              </div>
              {cat.description && (
                <p className="text-foreground-500 text-xs leading-relaxed mb-3 line-clamp-2">{cat.description}</p>
              )}

              <div className="flex items-center gap-1.5 mt-2">
                <button
                  onClick={() => openEdit(cat)}
                  className="flex-1 flex items-center justify-center gap-1 text-xs border border-background-300 rounded-md py-1.5 text-foreground-600 hover:text-primary-500 hover:border-primary-300 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-edit-line text-xs"></i> Editar
                </button>
                <button
                  onClick={() => toggleActive(cat.id)}
                  title={cat.active ? 'Inativar' : 'Ativar'}
                  className={`flex items-center justify-center w-8 h-7 rounded-md border cursor-pointer transition-colors ${
                    cat.active
                      ? 'border-background-300 text-foreground-500 hover:border-orange-300 hover:text-orange-500'
                      : 'border-green-200 text-green-600 hover:bg-green-50'
                  }`}
                >
                  <i className={cat.active ? 'ri-eye-off-line text-xs' : 'ri-eye-line text-xs'}></i>
                </button>
                {!DEFAULT_CATEGORY_IDS.includes(cat.id) && (
                  <button
                    onClick={() => setDeleteConfirm(cat.id)}
                    className="flex items-center justify-center w-8 h-7 rounded-md border border-background-300 text-foreground-400 hover:border-red-300 hover:text-red-500 cursor-pointer transition-colors"
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-background-50 rounded-xl p-6 max-w-sm w-full border border-background-200">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100 mb-4">
              <i className="ri-delete-bin-line text-xl text-red-500"></i>
            </div>
            <h3 className="font-bold text-foreground-950 text-lg mb-2">Excluir categoria?</h3>
            <p className="text-foreground-500 text-sm mb-6">
              Posts associados a esta categoria ainda a manterão registrada, mas ela não aparecerá mais na navegação.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2 text-sm border border-background-300 rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button
                onClick={() => { deleteCategory(deleteConfirm); setDeleteConfirm(null); }}
                className="flex-1 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-md cursor-pointer whitespace-nowrap font-bold"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DEFAULT_CATEGORY_IDS = ['saude', 'comportamento', 'alimentacao', 'racas', 'curiosidades', 'adocao'];